import { ChangeDetectionStrategy, Component, computed, model, OnInit, signal } from '@angular/core';
import { ProjectHeadVO } from '../model/vo/project.vo';
import { ArticleResultListComponent, ArticleTableColumn, ArticleTableData } from '@article/components/article-result-list/article-result-list.component';
import { ProjectDataService } from '../service/project-data.service';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { lastValueFrom } from 'rxjs';
import { ModalComponent } from '@shared/modal/modal.component';
import { TranslateModule } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DropdownComponent } from '@shared/dropdown/dropdown.component';
import { unique } from '@shared/util/array-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { NoItemWrapperDirective } from '@shared/no-item-wrapper.directive';

const propertyNames = [
  ArticleTechnicalInfoPropertyDef.COLOR,
];

type PreferredArticleTableData = ArticleTableData & {
  article: {
    displayName: string;
    color: any;
    [key: string]: any;
  }
}

@Component({
  selector: 'app-preferred-samples-modal',
  imports: [
    ArticleResultListComponent,
    ModalComponent,
    TranslateModule,
    DropdownComponent,
    NoItemWrapperDirective,
  ],
  templateUrl: './preferred-samples-modal.component.html',
  styleUrl: './preferred-samples-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreferredSamplesModalComponent implements OnInit {

  public readonly project!: ProjectHeadVO;

  public readonly selectedArticle = model<PreferredArticleTableData>();

  public readonly tableData = signal<PreferredArticleTableData[]>([]);
  public readonly tableColumns: ArticleTableColumn[] = [
    {title: 'viewArticlesPopup.dataGrid.info', dataKey: 'displayName'},
    {title: 'technicalInfoProperty.2', dataKey: 'color'},
    {title: 'configurationCreation.progress.mcsgds_series', dataKey: 'articleNo'},
  ];
  public readonly colors = computed(() => {
    return [ undefined, ...this.tableData().map((tableData) => tableData.article.color).filter(unique) ];
  });
  public readonly filterData = signal<Record<string, any>>({});
  public readonly filteredTableData = computed(() => {
    const filterData = this.filterData();
    return this.tableData().filter((tableData) => {
      for (const key in filterData) {

        if (filterData[key] && tableData.article[key] !== filterData[key]) {
          return false;
        }
      }

      return true;
    });
  });

  constructor(
    private bsModalRef: BsModalRef,
    private projectDataService: ProjectDataService,
    private localeResourceManager: LocaleResourceManager,
  ) {
  }

  public async ngOnInit(): Promise<void> {
    const preferredSamples = await lastValueFrom(this.projectDataService.getPreferredSamples(this.project.id, propertyNames));
    const tableData = preferredSamples.map<PreferredArticleTableData>((preferredSample) => ({
      article: {
        displayName: preferredSample.displayName,
        color: preferredSample.properties[ArticleTechnicalInfoPropertyDef.COLOR]?.value,
        mainProductFunction: preferredSample.properties[ArticleTechnicalInfoPropertyDef.MAIN_PRODUCT_FUNCTION]?.value,
        articleNo: preferredSample.articleNo,
      },
      image: ArticlePreviewUtils.getPreviewImageByNo(preferredSample.articleNo),
    }));
    this.tableData.set(tableData);
  }

  public cancel() {
    this.bsModalRef.hide();
  }

  public next() {
    const selectedArticle = this.selectedArticle();
    if (selectedArticle) {
      this.openShopLink(selectedArticle.article.articleNo);
    }
  }

  public selectColor(value: any) {
    this.filterData.update((filter) => ({...filter, color: value}));
  }

  public openShopLink(articleNo: string) {
    ArticlePreviewUtils.openShopLink(articleNo, this.localeResourceManager);
  }
}
